/**
 * 8b · Social Profile Duplicate Detection.
 *
 * Shows social URLs shared by 2+ realtors where at least one instance
 * is verified. Groups by URL with the verified owner as a read-only
 * anchor. Supports bulk deletion and "Mark as Shared Profile" whitelisting.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users, Trash2, Share2, Shield, ChevronDown, ChevronRight,
  Loader2, RefreshCw, ExternalLink, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const PLATFORM_ICON = {
  Facebook: "fb", Instagram: "ig", LinkedIn: "li",
  "X / Twitter": "tw", TikTok: "tk", YouTube: "yt", Threads: "th",
};

export default function SocialDupesSection({ onChange, onCountChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});        // groupIdx -> Set<realtor_id>
  const [confirmGroup, setConfirmGroup] = useState(null); // group index to confirm remove
  const [shareConfirm, setShareConfirm] = useState(null); // group index to confirm share
  const [busy, setBusy] = useState(false);
  const [showShared, setShowShared] = useState(false);
  const [sharedList, setSharedList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleanup/social-dupes");
      setGroups(data.groups || []);
      setSelected({});
      const actionable = (data.groups || []).filter((g) => g.count > 0).length;
      onCountChange?.(actionable);
    } catch (e) {
      toast.error("Failed to scan duplicates", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (gIdx, realtorId) => {
    setSelected((prev) => {
      const s = new Set(prev[gIdx] || []);
      if (s.has(realtorId)) s.delete(realtorId);
      else s.add(realtorId);
      return { ...prev, [gIdx]: s };
    });
  };

  const toggleSelectAll = (gIdx, dupes) => {
    setSelected((prev) => {
      const s = new Set(prev[gIdx] || []);
      const allSelected = dupes.every((d) => s.has(d.id));
      if (allSelected) {
        dupes.forEach((d) => s.delete(d.id));
      } else {
        dupes.forEach((d) => s.add(d.id));
      }
      return { ...prev, [gIdx]: s };
    });
  };

  const handleRemove = async () => {
    if (confirmGroup == null) return;
    const g = groups[confirmGroup];
    const sel = selected[confirmGroup] || new Set();
    if (sel.size === 0) return;
    setBusy(true);
    try {
      const items = [...sel].map((rid) => {
        const d = g.duplicates.find((x) => x.id === rid);
        return {
          realtor_id: rid,
          field: g.field,
          owner_id: g.owners[0]?.id || "",
          source: d?.source || "stored",
        };
      });
      const { data } = await api.post("/cleanup/social-dupes/remove", { items });
      toast.success(`Removed ${data.removed} duplicate link${data.removed === 1 ? "" : "s"}`);
      setConfirmGroup(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Remove failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const handleMarkShared = async () => {
    if (shareConfirm == null) return;
    const g = groups[shareConfirm];
    setBusy(true);
    try {
      await api.post("/cleanup/social-dupes/mark-shared", {
        url: g.url, field: g.field,
      });
      toast.success("Marked as shared profile — hidden from duplicates");
      setShareConfirm(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const loadShared = async () => {
    try {
      const { data } = await api.get("/cleanup/social-dupes/shared");
      setSharedList(data.items || []);
    } catch { /* silent */ }
  };

  const handleUnshare = async (url, field) => {
    try {
      await api.post("/cleanup/social-dupes/unshare", { url, field });
      toast.success("Removed from shared list");
      loadShared();
      load();
      onChange?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="space-y-4" data-testid="social-dupes-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-slate-600">
          Social URLs shared by multiple realtors where at least one instance is verified.
          Remove duplicates or mark as intentionally shared (brokerage/team pages).
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setShowShared((v) => !v); if (!showShared) loadShared(); }}
            className="text-xs"
            data-testid="social-dupes-toggle-shared"
          >
            <Share2 className="h-3.5 w-3.5 mr-1" />
            {showShared ? "Hide shared list" : "Shared list"}
          </Button>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            disabled={loading}
            className="shrink-0 min-h-[44px]"
            data-testid="social-dupes-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Shared whitelist tray */}
      {showShared && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="social-dupes-shared-list">
          <div className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1.5">
            <Share2 className="h-3.5 w-3.5" /> Shared profiles (whitelisted)
          </div>
          {sharedList.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No URLs marked as shared yet.</div>
          ) : (
            <div className="space-y-1">
              {sharedList.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-[10px]">{s.platform}</Badge>
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    className="font-mono text-slate-700 hover:underline truncate flex-1">
                    {s.url}
                  </a>
                  <span className="text-slate-400 shrink-0">by {s.marked_by} on {new Date(s.marked_at).toLocaleDateString()}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-500 hover:text-rose-700"
                    onClick={() => handleUnshare(s.url, s.field)} data-testid={`unshare-${i}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && groups.length === 0 ? (
        <div className="text-sm text-slate-500 italic flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning for duplicate social profiles…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2"
          data-testid="social-dupes-empty">
          <Check className="h-4 w-4" /> No duplicate social profiles found. All verified links are unique.
        </div>
      ) : (
        <div className="space-y-3">
          {/* Actionable groups (have removable duplicates) */}
          {groups.filter((g) => g.count > 0).length > 0 && (
            <>
              {groups.filter((g) => g.count > 0).map((g) => {
                const gIdx = groups.indexOf(g);
                return (
                  <DupeGroupCard
                    key={`${g.field}-${g.url}`}
                    group={g}
                    gIdx={gIdx}
                    selected={selected[gIdx] || new Set()}
                    onToggle={(rid) => toggleSelect(gIdx, rid)}
                    onToggleAll={() => toggleSelectAll(gIdx, g.duplicates)}
                    onRemove={() => setConfirmGroup(gIdx)}
                    onMarkShared={() => setShareConfirm(gIdx)}
                  />
                );
              })}
            </>
          )}
          {groups.filter((g) => g.count > 0).length === 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
              <Check className="h-4 w-4" /> No actionable duplicates — all shared URLs have verified owners on both sides.
            </div>
          )}

          {/* All-verified groups (both realtors verified — implicit shared) */}
          {groups.filter((g) => g.count === 0).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Both verified — consider marking as shared ({groups.filter((g) => g.count === 0).length})
              </div>
              {groups.filter((g) => g.count === 0).map((g) => {
                const gIdx = groups.indexOf(g);
                return (
                  <SharedOwnerCard
                    key={`${g.field}-${g.url}`}
                    group={g}
                    gIdx={gIdx}
                    onMarkShared={() => setShareConfirm(gIdx)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Confirm Remove Dialog */}
      <AlertDialog open={confirmGroup != null} onOpenChange={(o) => { if (!o && !busy) setConfirmGroup(null); }}>
        <AlertDialogContent data-testid="social-dupes-confirm-remove">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove duplicate social links?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-2">
                  This will blank the <strong>{confirmGroup != null ? groups[confirmGroup]?.platform : ""}</strong> URL
                  on the following realtors and log it as <code>duplicate_deleted</code>:
                </p>
                <ul className="list-disc pl-5 space-y-0.5 text-sm">
                  {confirmGroup != null && [...(selected[confirmGroup] || [])].map((rid) => {
                    const d = groups[confirmGroup]?.duplicates?.find((x) => x.id === rid);
                    return <li key={rid}>{d?.name || rid}</li>;
                  })}
                </ul>
                <p className="mt-2 text-xs text-slate-500">
                  Verified owner ({confirmGroup != null ? groups[confirmGroup]?.owners?.[0]?.name : ""}) is not affected.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="social-dupes-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="social-dupes-confirm-apply"
            >
              {busy ? "Removing…" : `Remove ${confirmGroup != null ? (selected[confirmGroup]?.size || 0) : 0} link${(selected[confirmGroup]?.size || 0) === 1 ? "" : "s"}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Mark as Shared Dialog */}
      <AlertDialog open={shareConfirm != null} onOpenChange={(o) => { if (!o && !busy) setShareConfirm(null); }}>
        <AlertDialogContent data-testid="social-dupes-confirm-share">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as shared profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This URL will be added to the shared whitelist and hidden from the duplicate list.
              Use this for brokerage pages, team accounts, or URLs intentionally shared by multiple realtors.
              You can undo this later from the shared list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkShared}
              disabled={busy}
              className="bg-slate-700 hover:bg-slate-800"
              data-testid="social-dupes-confirm-share-apply"
            >
              {busy ? "Saving…" : "Mark as shared"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function DupeGroupCard({ group: g, gIdx, selected, onToggle, onToggleAll, onRemove, onMarkShared }) {
  const [expanded, setExpanded] = useState(true);
  const allSelected = g.duplicates.length > 0 && g.duplicates.every((d) => selected.has(d.id));
  const someSelected = g.duplicates.some((d) => selected.has(d.id));

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid={`dupe-group-${gIdx}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
        data-testid={`dupe-group-toggle-${gIdx}`}
      >
        <Users className="h-4 w-4 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] font-medium">{g.platform}</Badge>
            <a href={g.url} target="_blank" rel="noopener noreferrer"
              className="font-mono text-xs text-slate-700 hover:underline truncate inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}>
              {g.url} <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>
        </div>
        <Badge className="bg-amber-100 text-amber-900 border-amber-200 shrink-0" variant="outline">
          {g.count} duplicate{g.count === 1 ? "" : "s"}
        </Badge>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {/* Verified owner(s) */}
          {g.owners.map((o) => (
            <div key={o.id} className="flex items-center gap-3 px-4 py-2.5 bg-emerald-50/60 border-b border-slate-100">
              {o.photo_url ? (
                <img src={o.photo_url} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 text-xs font-bold shrink-0">
                  {(o.name || "?")[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <Link to={`/realtors/${o.id}`} className="text-sm font-medium text-slate-900 hover:underline">
                  {o.name}
                </Link>
                {o.brokerage_name && <div className="text-xs text-slate-500 truncate">{o.brokerage_name}</div>}
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1 shrink-0" variant="outline">
                <Shield className="h-3 w-3" /> Verified owner
              </Badge>
            </div>
          ))}

          {/* Duplicates with checkboxes */}
          {g.duplicates.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => onToggleAll()}
                data-testid={`dupe-group-select-all-${gIdx}`}
              />
              <span className="text-xs text-slate-500">
                {allSelected ? "Deselect all" : "Select all"} ({g.duplicates.length})
              </span>
            </div>
          )}
          {g.duplicates.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
              <Checkbox
                checked={selected.has(d.id)}
                onCheckedChange={() => onToggle(d.id)}
                data-testid={`dupe-check-${gIdx}-${d.id}`}
              />
              {d.photo_url ? (
                <img src={d.photo_url} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-bold shrink-0">
                  {(d.name || "?")[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Link to={`/realtors/${d.id}`} className="text-sm font-medium text-slate-900 hover:underline">
                    {d.name}
                  </Link>
                  {d.source === "pending" && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" variant="outline">
                      in review queue
                    </Badge>
                  )}
                </div>
                {d.brokerage_name && <div className="text-xs text-slate-500 truncate">{d.brokerage_name}</div>}
              </div>
            </div>
          ))}

          {/* Actions bar */}
          <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-t border-slate-100">
            <Button
              size="sm"
              variant="destructive"
              disabled={!someSelected}
              onClick={onRemove}
              className="min-h-[36px]"
              data-testid={`dupe-group-remove-${gIdx}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove from selected ({selected.size})
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onMarkShared}
              className="text-slate-600 min-h-[36px]"
              data-testid={`dupe-group-share-${gIdx}`}
            >
              <Share2 className="h-3.5 w-3.5 mr-1" />
              Mark as shared profile
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function SharedOwnerCard({ group: g, gIdx, onMarkShared }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden mb-2" data-testid={`shared-owner-group-${gIdx}`}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Users className="h-4 w-4 text-slate-400 shrink-0" />
        <Badge variant="outline" className="text-[10px] font-medium">{g.platform}</Badge>
        <a href={g.url} target="_blank" rel="noopener noreferrer"
          className="font-mono text-xs text-slate-600 hover:underline truncate flex-1 inline-flex items-center gap-1">
          {g.url} <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
          {g.owners.map((o) => (
            <Link key={o.id} to={`/realtors/${o.id}`} className="hover:underline text-slate-700">
              {o.name}
            </Link>
          )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={`sep-${i}`}>&</span>, curr], [])}
        </div>
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0" variant="outline">
          both verified
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={onMarkShared}
          className="text-slate-500 h-7 text-xs shrink-0"
          data-testid={`shared-owner-share-${gIdx}`}
        >
          <Share2 className="h-3 w-3 mr-1" /> Mark shared
        </Button>
      </div>
    </div>
  );
}
